import { useQuery } from '@tanstack/react-query';
import type { RequestOptions } from '@/services/apiClient';
import { sessionScopeKey, useSessionScopeFence } from './useSessionScope';

/** Numeric reports belong to the initiating tenant, actor, grants and sign-in. */
export function useScopedNumericQuery<T>(
  key: readonly unknown[],
  permission: string | undefined,
  loader: (options: RequestOptions) => Promise<T>,
  options: { enabled?: boolean; staleTime?: number; refetchInterval?: number } = {},
) {
  const { identity, enabled: sessionEnabled, isCurrent } = useSessionScopeFence(permission);
  const enabled = sessionEnabled && options.enabled !== false;
  const query = useQuery({
    queryKey: [...key, ...sessionScopeKey(identity)],
    enabled,
    placeholderData: () => undefined,
    staleTime: options.staleTime ?? 30_000,
    refetchInterval: options.refetchInterval,
    retry: false,
    queryFn: async ({ signal }) => {
      if (!enabled || !isCurrent(identity)) throw new Error('Current access is required to load these figures.');
      const result = await loader({ signal, sessionOrigin: identity.sessionOrigin });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw new Error('Access changed while these figures were loading.');
      return result;
    },
  });
  return { ...query, data: enabled && !query.isError ? query.data : undefined };
}
